import pick from 'lodash/pick';
import tuner from '@tunein/web-tuner';

import ad from 'src/common/constants/analytics/categoryActionLabel/ad';
import { MINT_ERROR, MINT_INFO } from '../constants/ads/logging';
import { PAUSE_PREROLL_ENABLED } from '../constants/experiments/ads';
import { getSliderPercentage } from '../utils/getSliderPercentage';
import * as PrerollActions from './preroll';
import * as TunerActions from './tuner';
import * as PlayerActions from './player';
import {
  logCategoryActionLabel,
  logClientError,
  logClientInfo,
} from './logging';
import { displaySlotLoaded } from './mint';

export default function mintEventDispatcher(dispatch, experiments) {
  return (mint) => {
    function onPrerollLoad() {
      dispatch(PrerollActions.loadPreroll());
    }

    function onPrerollStart(dimensions) {
      const heightAndWidth = pick(dimensions, ['height', 'width'], {});
      dispatch(
        PrerollActions.handlePreroll(
          heightAndWidth.width,
          heightAndWidth.height,
        ),
      );
      if (experiments[PAUSE_PREROLL_ENABLED]) {
        dispatch(PrerollActions.playPreroll());
      }
    }

    function onVolumeUpdate(volume) {
      dispatch(PrerollActions.updateVolume(volume));
    }

    function onLoaded() {
      dispatch(PrerollActions.showPrerollPlayer());
    }

    function onPlaying() {
      if (experiments[PAUSE_PREROLL_ENABLED]) {
        dispatch(TunerActions.playingAd());
      } else {
        dispatch(PrerollActions.handlePrerollResume());
      }
    }

    function onPaused() {
      if (experiments[PAUSE_PREROLL_ENABLED]) {
        dispatch(PlayerActions.handlePause());
      } else {
        dispatch(PrerollActions.handlePrerollPause());
      }
    }

    function onProgress(timeData = {}) {
      const { duration, position: elapsedSeconds } = timeData;
      const elapsedPercent = getSliderPercentage(elapsedSeconds, duration);

      dispatch(
        PrerollActions.handlePrerollProgress({
          duration,
          elapsedSeconds,
          elapsedPercent,
        }),
      );
    }

    function onEnded() {
      dispatch(PrerollActions.hidePrerollPlayer());
    }

    function onSetLearnMore(url) {
      dispatch(PrerollActions.setLearnMoreUrl(url));
    }

    function onMintError(error) {
      const data = {
        message: MINT_ERROR,
        context: { error },
      };
      dispatch(logClientError(data));
    }

    function onMintPrerollError(error) {
      onMintError(error);
      dispatch(PrerollActions.handlePrerollError());
    }

    function onMintInfo(info) {
      const data = {
        message: MINT_INFO,
        context: { info },
      };
      dispatch(logClientInfo(data));
    }

    function onDisplayAdPaused(adSizeString) {
      dispatch(
        logCategoryActionLabel({
          category: ad.category,
          action: adSizeString,
          label: ad.labels.pause,
        }),
      );
    }

    function onDisplayAdResumed(adSizeString) {
      dispatch(
        logCategoryActionLabel({
          category: ad.category,
          action: adSizeString,
          label: ad.labels.resume,
        }),
      );
    }

    function onDisplayAdLoaded({ elementId }) {
      dispatch(displaySlotLoaded(elementId));
    }

    // when mint finishes with the last ad, it emits an event to tell the
    // tuner to start polling again for more ad data
    function onInstreamAdRequest() {
      tuner.requestInstreamAds(); // send message to web-tuner
    }

    // before trying to play the stream, the instream player has to make a
    // post request with the url and pass it these params (from mint)
    function onInstreamUpdateParams(custParams) {
      tuner.setInstreamCustParams(custParams); // send message to web-tuner
    }

    let mintEventsAndCallbacks;

    function generateEventsAndCallbacks() {
      const {
        Type: { preroll, instream },
        Media: { audio, video, display },
        Logging: { info, error },
        Status: {
          loaded,
          loading,
          playing,
          paused,
          ended,
          progress,
          volume,
          setLearnMore,
          resumed,
          requestInstreamAds,
          updateInstreamParams,
        },
      } = mint.Events;

      mintEventsAndCallbacks = [
        // Preroll Events
        { events: [preroll, loaded], callback: onPrerollStart },
        { events: [preroll, loading], callback: onPrerollLoad },
        { events: [preroll, audio, progress], callback: onProgress },

        // Preroll Video Events
        { events: [preroll, video, volume], callback: onVolumeUpdate },
        { events: [preroll, video, loaded], callback: onLoaded },
        { events: [preroll, video, playing], callback: onPlaying },
        { events: [preroll, video, paused], callback: onPaused },
        { events: [preroll, video, progress], callback: onProgress },
        { events: [preroll, video, ended], callback: onEnded },
        { events: [preroll, video, setLearnMore], callback: onSetLearnMore },

        // Error Event
        { events: [error], callback: onMintError },
        { events: [preroll, error], callback: onMintPrerollError },

        // Info Event
        { events: [info], callback: onMintInfo },

        // Instream Event
        {
          events: [instream, requestInstreamAds],
          callback: onInstreamAdRequest,
        },
        {
          events: [instream, updateInstreamParams],
          callback: onInstreamUpdateParams,
        },

        // Display Ad Event
        { events: [display, loaded], callback: onDisplayAdLoaded },
      ];

      if (experiments[PAUSE_PREROLL_ENABLED]) {
        mintEventsAndCallbacks.push(
          { events: [preroll, audio, playing], callback: onPlaying },
          { events: [preroll, audio, paused], callback: onPaused },
          { events: [preroll, audio, ended], callback: onEnded },

          // display Events for reporting
          { events: [display, paused], callback: onDisplayAdPaused },
          { events: [display, resumed], callback: onDisplayAdResumed },
        );
      }
    }

    function handleEventListenerRegistration(action) {
      mintEventsAndCallbacks.forEach(({ events, callback }) => {
        mint.event(...events)[action](callback);
      });
    }

    function addListeners() {
      handleEventListenerRegistration('listen');
    }

    function removeListeners() {
      handleEventListenerRegistration('remove');
    }

    function init() {
      generateEventsAndCallbacks();
      addListeners();
    }

    init();
    return removeListeners;
  };
}
